import React from 'react';
import classNames from 'classnames';

import { InstagramEmbed } from 'react-social-media-embed';
import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionInstagram.module.css';

// Dimensions passed to the Instagram embed (controls each post card's size)
const EMBED_WIDTH = '100%';
const EMBED_HEIGHT = 320;

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'columns'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionInstagram = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasCta = hasDataInFields([callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  const secondaryCtaData = {
    fieldType: 'externalButtonLink',
    href: 'https://www.instagram.com/pack2godeals',
    content: 'Follow @pack2godeals',
  };

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.layout}>
        {hasHeaderFields ? (
          <header className={classNames(css.sectionDetails, defaultClasses.sectionDetails)}>
            <Field
              data={title}
              className={classNames(defaultClasses.title, css.title)}
              options={fieldOptions}
            />
            <Field
              data={description}
              className={classNames(defaultClasses.description, css.description)}
              options={fieldOptions}
            />
            {hasCta ? (
              <Field
                data={callToAction}
                className={classNames(defaultClasses.ctaButton, css.followCta)}
                options={fieldOptions}
              />
            ) : (
              <Field data={secondaryCtaData} className={css.followCta} options={fieldOptions} />
            )}
          </header>
        ) : null}

        {hasBlocks ? (
          <div
            className={classNames(css.embeds, getColumnCSS(numColumns), {
              [css.noSidePaddings]: isInsideContainer,
            })}
          >
            {blocks.map((block, index) => {
              const link = block?.callToAction?.href;
              if (!link) {
                return null;
              }
              return (
                <div key={block?.blockId || link} className={css.card}>
                  <InstagramEmbed
                    url={link}
                    width={EMBED_WIDTH}
                    height={EMBED_HEIGHT}
                    captioned={false}
                    // Disable the library's 5s reprocess-retry loop. On slow/failed
                    // embeds it remounts the <blockquote> and re-runs
                    // window.instgrm.Embeds.process(), which makes Instagram's
                    // embed.js throw "Invariant Violation #4412" as its internal
                    // React operates on a node that was swapped out mid-process.
                    retryDisabled
                    className={css.post}
                  />
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </SectionContainer>
  );
};

export default SectionInstagram;
